import { useLanguage } from "src/context/LanguageContext";
import { Button } from "src/components/ui/button";
import { ArrowUpDown, Edit2, Eye, PlusCircle, Trash2 } from "lucide-react";
import { DataTable } from "src/components/DataTable";
import { useAuth } from "src/context/AuthContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "src/lib/api";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Avatar, Spin } from "antd";
import { Badge } from "src/components/ui/badge";
import { cn } from "../../../lib/utils";
import { UrlImage } from "../../../lib/api";
import MainButton from "../../../components/MainButton";
import { useNavigate } from "react-router-dom";
import DialogDelete from "../../../components/common/DialogDelete";
import ProfileDialog from "./ProfileDialog";

const UsersPage = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [errors, setErrors] = useState({});

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/users`);
      setUsers(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const columns = [
    {
      accessorKey: `name`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Name
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Avatar
            className={cn(
              "min-w-8 min-h-8 rounded-full flex items-center justify-center text-xs font-medium",
              "bg-blue-100 text-blue-600",
              "dark:bg-blue-900 dark:text-blue-300"
            )}
            src={`${UrlImage}${row.original.image}`}
          >
            {row.original.name.charAt(0)}
          </Avatar>
          <div className="text-start">
            <div className="font-medium">{row.original.name}</div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: "user_name",
      header: "Username",
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "phone",
      header: "Phone",
    },
    {
      accessorKey: "role",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Role
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ getValue }) => {
        const role = getValue();
        const label = role.charAt(0).toUpperCase() + role.slice(1);
        // admin = red, teacher = gray, you can adjust for other roles
        const variant = role === "admin" ? "destructive" : "secondary";
        return <Badge variant={variant}>{label}</Badge>;
      },
    },
    {
      accessorKey: "Actions",
      cell: ({ row }) => {
        const profile = row.original;

        const urlNavigate =
          profile.role === "teacher"
            ? "/admin/teacher-settings"
            : profile.role === "student" && "/admin/settings";
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() =>
                navigate(urlNavigate, { state: { userData: profile } })
              }
            >
              <Eye />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setOpen(true);
                setProfileData(profile);
              }}
            >
              <Edit2 />
            </Button>
            <DialogDelete
              apiLink={`/users/${profile?.id}`}
              onSuccess={fetchUsers}
              itemTitle={profile.name}
              name={"User"}
            >
              <Button variant="ghost" size="icon">
                <Trash2 />
              </Button>
            </DialogDelete>
          </div>
        );
      },
    },
  ];

  const handleProfile = async (formData) => {
    setLoading(true);
    setErrors({});
    try {
      if (profileData?.id) {
        await axios.post(`${BaseUrlApi}/users/${profileData?.id}`, formData);
        toast("Profile saved", { status: "success" });
      } else {
        await axios.post(`${BaseUrlApi}/users`, formData);
        toast("Profile added", { status: "success" });
      }
      await fetchUsers();
      setOpen(false);
    } catch (error) {
      const msg = ErrorMessage(error);
      if (typeof msg === "string")
        toast("Error", { description: msg, status: "error" });
      setErrors(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center gap-2 justify-between flex-wrap">
        <h1 className="text-2xl font-bold">Users</h1>
        <MainButton
          Icon={PlusCircle}
          title={"Add User"}
          onClick={() => {
            setProfileData({});
            setOpen(true);
          }}
        />
        <ProfileDialog
          open={open}
          onOpenChange={setOpen}
          profileData={profileData}
          saving={loading}
          setSaving={setLoading}
          onSave={handleProfile}
          errors={errors}
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={users}
          title={"users by name & email"}
          filterNames={["name", "email"]}
        />
      )}
    </div>
  );
};

export default UsersPage;
